import { ElementType, useMemo } from "react"
import { Controller, useFormContext } from "react-hook-form"
import { Dropdown, FormField as GoDesignFormField, MultiSelectDropdown } from "@gogoro/go-design"
import classnames from "classnames"
import get from "lodash.get"

import StringDatePicker from "./DatePicker/StringDatePicker"
import StringDateRangePicker from "./DateRangePicker/StringDateRangePicker"
import AsyncSelect from "./AsyncSelect"
import CheckboxGroup from "./CheckboxGroup"
import DatePicker from "./DatePicker"
import DateRangePicker from "./DateRangePicker"
import { FormFieldProps } from "./FormFieldProps"
import PasswordInput from "./PasswordInput"
import PureText from "./PureText"
import RadioGroup from "./RadioGroup"
import ReactSelect from "./ReactSelect"
import SearchInput from "./SearchInput"
import Textarea from "./Textarea"
import TextInput from "./TextInput"

const FormField = ({
  rootClassName,
  label,
  name = "",
  type,
  required = false,
  showError = true,
  onChange: customizedOnChange,
  defaultValue = null,
  ...restProps
}: FormFieldProps) => {
  const formContext = useFormContext() || {}

  const { formState, control } = formContext

  const errors = formState?.errors
  const error = get(errors, `${name}.message`)

  const CurrentField: ElementType = useMemo(() => {
    switch (type) {
      case "text": {
        return TextInput
      }
      case "pure-text": {
        return PureText
      }
      case "textarea": {
        return Textarea
      }
      case "select": {
        return Dropdown
      }
      case "multi-select": {
        return MultiSelectDropdown
      }
      case "smart-search": {
        return ReactSelect
      }
      case "async-select": {
        return AsyncSelect
      }
      case "date-range": {
        return DateRangePicker
      }
      case "string-date-range":
        return StringDateRangePicker
      case "date": {
        return DatePicker
      }
      case "string-date": {
        return StringDatePicker
      }
      case "checkbox": {
        return CheckboxGroup
      }
      case "radio": {
        return RadioGroup
      }
      case "search-input": {
        return SearchInput
      }
      case "password": {
        return PasswordInput
      }
      default: {
        return TextInput
      }
    }
  }, [type])

  return (
    <GoDesignFormField
      rootClassName={classnames(rootClassName, { "has-error": !!error })}
      label={label}
      errorMessage={showError ? error : ""}
      required={required}>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field: { onChange, onBlur, value, ref } }) => {
          const finalValue = value === null || value === undefined ? "" : value

          const handleOnChange = (value: unknown, e: any) => {
            onChange && onChange(value)
            customizedOnChange && customizedOnChange(value, e)
          }

          return (
            <CurrentField
              name={name}
              onChange={handleOnChange}
              onBlur={onBlur}
              value={finalValue}
              {...restProps}
              error={!!error}
              ref={ref}
            />
          )
        }}
      />
    </GoDesignFormField>
  )
}

export default FormField
